import type {CommercialAvailability, PublicationStatus} from '@dms/api/new-vehicle';

import {parseDate} from 'shared/web';

type Trim = {
  publicationStatus: PublicationStatus;
  commercialAvailability: CommercialAvailability;
  orderableFrom?: string | null;
  orderableUntil?: string | null;
};

export type TrimOrderability = {
  orderable: boolean;
  reasonKey?:
    | 'previewNotOrderable'
    | 'soldOut'
    | 'orderingEnded'
    | 'orderingStarts'
    | 'publicationDraft'
    | 'publicationArchived';
  date?: Date;
};

type Translate = (key: string, defaultValue: string) => string;
type TranslateWithDate = (key: string, options: {defaultValue: string; date: string}) => string;

const reasonLabels = {
  previewNotOrderable: 'Preview — not yet orderable',
  soldOut: 'Sold out',
  orderingEnded: 'No longer orderable',
  orderingStarts: 'Orderable from',
  publicationDraft: 'Not yet published',
  publicationArchived: 'Archived',
} as const;

export function getTrimOrderabilityLabel(result: TrimOrderability, t: Translate) {
  if (!result.reasonKey) {
    return t('entity.newVehicle.enums.trimOrderability.orderable', 'Orderable');
  }
  return t(
    `entity.newVehicle.enums.trimOrderability.${result.reasonKey}`,
    reasonLabels[result.reasonKey]
  );
}

export function getTrimOrderabilityDateLabel(
  result: TrimOrderability,
  formattedDate: string,
  t: TranslateWithDate
) {
  if (result.reasonKey === 'orderingEnded') {
    return t('entity.newVehicle.enums.trimOrderability.orderingEndedWithDate', {
      defaultValue: 'No longer orderable · ended {date}',
      date: formattedDate,
    });
  }
  return t('entity.newVehicle.enums.trimOrderability.orderingStartsWithDate', {
    defaultValue: 'Orderable from {date}',
    date: formattedDate,
  });
}

export function getTrimOrderability(trim: Trim, today: Date): TrimOrderability {
  if (trim.publicationStatus !== 'online') {
    return {
      orderable: false,
      reasonKey: trim.publicationStatus === 'archived' ? 'publicationArchived' : 'publicationDraft',
    };
  }
  if (trim.commercialAvailability === 'preview') {
    return {orderable: false, reasonKey: 'previewNotOrderable'};
  }
  if (trim.commercialAvailability === 'sold_out') {
    return {orderable: false, reasonKey: 'soldOut'};
  }

  const currentDate = parseDate(today.toISOString().slice(0, 10));
  const orderableFrom = trim.orderableFrom ? parseDate(trim.orderableFrom) : undefined;
  const orderableUntil = trim.orderableUntil ? parseDate(trim.orderableUntil) : undefined;
  if (orderableUntil && orderableUntil < currentDate) {
    return {orderable: false, reasonKey: 'orderingEnded', date: orderableUntil};
  }
  if (orderableFrom && orderableFrom > currentDate) {
    return {orderable: false, reasonKey: 'orderingStarts', date: orderableFrom};
  }
  return {orderable: trim.commercialAvailability === 'orderable'};
}
